import { ReactNode, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { ButtonV1 } from '@northladder/design-system';
import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import {
  AssessmentHeader,
  AssessmentModal,
  FeedbackMessage,
  IAssessmentModalOptions,
  IFeedbackMessageOptions,
  StepsIndicator,
} from '~/features';

import { PageLayout } from '../PageLayout';

interface IActionCTA {
  label: TTextLocalized;
  labelLoading?: TTextLocalized;
  onClick: () => void;
  isEnabled?: boolean;
}

interface IActionModalCTA extends IActionCTA {
  modalContent?: IAssessmentModalOptions;
}

export interface IActionItems {
  primaryCTA?: IActionCTA;
  secondaryCTA?: IActionModalCTA;
  labelCTA?: IActionModalCTA;
}

interface IAssessmentNotWorkingModalTriggerProps {
  type: 'label-btn' | 'secondary-btn';
  actionItem: IActionModalCTA;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a simple button styled as a label which when clicked will open
 * a modal with the content provided in the `modalContent` prop or just render
 * a single button if no `modalContent` is provided.
 */
function AssessmentLabelCTAModalTrigger({
  type,
  actionItem,
}: IAssessmentNotWorkingModalTriggerProps) {
  const { label, onClick, modalContent } = actionItem;

  if (!modalContent) {
    return (
      <button
        className={clsx('cta-button', type)}
        onClick={onClick}
        type="button"
      >
        <Text text={label} />
      </button>
    );
  }

  return (
    <AssessmentModal
      {...modalContent}
      renderModalTrigger={({ onOpenModal }) => (
        <button
          className={clsx('cta-button', type)}
          onClick={onOpenModal}
          type="button"
        >
          <Text text={label} />
        </button>
      )}
    />
  );
}

interface IAssessmentActionsProps {
  actions: IActionItems;
  isCTALoading?: boolean;
}

/**
 * -----------------------------------------------------------------------------
 * This displays the set of actions at the bottom of the assessment page which
 * the user can take. The `primary` action is the main action the user should
 * take to proceed to the next step. The `secondary` action is an alternative
 * action and the `label` action is taken for opt out cases.
 */
function AssessmentActions({ actions, isCTALoading }: IAssessmentActionsProps) {
  const [isLoading, setIsLoading] = useState(false);

  const { primaryCTA, secondaryCTA, labelCTA } = actions;

  const handleOnPrimaryCTAClick = () => {
    if (!primaryCTA) return;

    primaryCTA?.onClick();

    if (isCTALoading === undefined) {
      setIsLoading(true);
    }
  };

  const isLoadingState = isCTALoading !== undefined ? isCTALoading : isLoading;

  return (
    <>
      {primaryCTA ? (
        <ButtonV1
          className="mx-auto block w-auto"
          isEnabled={primaryCTA.isEnabled || !isLoadingState}
          isLoading={isLoadingState}
          label={primaryCTA.label}
          loadingLabel={primaryCTA?.labelLoading}
          onClick={handleOnPrimaryCTAClick}
          type="button"
        />
      ) : null}

      {secondaryCTA ? (
        <AssessmentLabelCTAModalTrigger
          actionItem={secondaryCTA}
          type="secondary-btn"
        />
      ) : null}

      {labelCTA ? (
        <AssessmentLabelCTAModalTrigger
          actionItem={labelCTA}
          type="label-btn"
        />
      ) : null}
    </>
  );
}

interface IAssessmentBaseLayoutPage {
  actions: IActionItems;
  classNameDiv?: string;
  classNameFifthImage?: string;
  classNameFourthImage?: string;
  classNameImage?: string;
  classNameSecondImage?: string;
  classNameSixthImage?: string;
  classNameThirdImage?: string;
  commonEvaluateDevice: TTextLocalized;
  description: TTextLocalized;
  feedbackOptions?: IFeedbackMessageOptions;
  heroComponent?: ReactNode;
  heroImageSrc?: string;
  heroSubComponent?: ReactNode;
  imageAlt?: string;
  imageFifthAlt?: string;
  imageFifthHeight?: number;
  imageFifthSrc?: string;
  imageFifthWidth?: number;
  imageFourthAlt?: string;
  imageFourthHeight?: number;
  imageFourthSrc?: string;
  imageFourthWidth?: number;
  imageHeight?: number;
  imageSecondAlt?: string;
  imageSecondHeight?: number;
  imageSecondSrc?: string;
  imageSecondWidth?: number;
  imageSixthAlt?: string;
  imageSixthHeight?: number;
  imageSixthSrc?: string;
  imageSixthWidth?: number;
  imageThirdAlt?: string;
  imageThirdHeight?: number;
  imageThirdSrc?: string;
  imageThirdWidth?: number;
  imageWidth?: number;
  isCTALoading?: boolean;
  overlayComponent?: ReactNode;
  page: number;
  pageTitle?: string;
  shouldDisableHeader?: boolean;
  stepActiveId: number; // `Merge with stepIndex`
  stepIndex: number;
  title: TTextLocalized;
}

/**
 * -----------------------------------------------------------------------------
 * The base underlying layout for the assessment step identifier page that outlines
 * a hero image of the feature to be assessed, a title, description and a set of
 * actions the user can take to proceed to the next step.
 */
export function AssessmentBaseLayoutPage({
  actions,
  classNameDiv,
  classNameFifthImage,
  classNameFourthImage,
  classNameImage,
  classNameSecondImage,
  classNameSixthImage,
  classNameThirdImage,
  commonEvaluateDevice,
  description,
  feedbackOptions,
  heroComponent,
  heroImageSrc,
  heroSubComponent,
  imageAlt,
  imageFifthAlt,
  imageFifthHeight,
  imageFifthSrc,
  imageFifthWidth,
  imageFourthAlt,
  imageFourthHeight,
  imageFourthSrc,
  imageFourthWidth,
  imageHeight,
  imageSecondAlt,
  imageSecondHeight,
  imageSecondSrc,
  imageSecondWidth,
  imageSixthAlt,
  imageSixthHeight,
  imageSixthSrc,
  imageSixthWidth,
  imageThirdAlt,
  imageThirdHeight,
  imageThirdSrc,
  imageThirdWidth,
  imageWidth,
  isCTALoading,
  overlayComponent,
  page,
  pageTitle,
  shouldDisableHeader = false,
  stepActiveId,
  stepIndex,
  title,
}: IAssessmentBaseLayoutPage) {
  return (
    <PageLayout stepIndex={stepIndex} title={pageTitle}>
      <div
        className={clsx(
          `to-primary-100 dark:to-primary-950/50 min-h-screen bg-gradient-to-b
          from-slate-50 dark:from-gray-800`,
          shouldDisableHeader && 'pt-10'
        )}
      >
        {!shouldDisableHeader ? <AssessmentHeader page={page} /> : null}

        <div className="flex h-full w-full flex-col items-center justify-center gap-2.5 overflow-hidden pb-6 pt-8">
          <div className="flex max-w-[343px] flex-col items-center justify-center gap-2 px-4">
            <p className="text-body-text text-base font-medium leading-tight">
              <Text text={commonEvaluateDevice} />
            </p>
            <h1 className="text-body-text text-2xl font-bold leading-8">
              <Text text={title} />
            </h1>
            <p
              className="
                text-primary-600 dark:text-primary-400 text-center text-sm font-bold
                leading-6 opacity-95
              "
            >
              <Text text={description} />
            </p>
          </div>
          <div className="pt-2">
            <StepsIndicator activeId={stepActiveId} />
          </div>

          {heroComponent ? (
            <div
              className={`
              border-primary-50 my-14 flex h-56 w-56 items-center justify-center
              rounded-full border-8 ${classNameDiv}
            `}
            >
              {heroComponent}
            </div>
          ) : null}

          {heroImageSrc && (
            <div
              className={`
                border-primary-50 my-14 flex h-56 w-56 items-center justify-center rounded-full
                border-8 ${classNameDiv}
              `}
            >
              <Image
                alt={imageAlt || ''}
                className={`${classNameImage} h-auto w-auto`}
                height={imageHeight}
                priority
                src={heroImageSrc}
                width={imageWidth}
              />
              {imageSecondSrc && (
                <Image
                  alt={imageSecondAlt || ''}
                  className={classNameSecondImage}
                  height={imageSecondHeight}
                  src={imageSecondSrc}
                  width={imageSecondWidth}
                />
              )}
              {imageThirdSrc && (
                <Image
                  alt={imageThirdAlt || ''}
                  className={classNameThirdImage}
                  height={imageThirdHeight}
                  src={imageThirdSrc}
                  width={imageThirdWidth}
                />
              )}
              {imageFourthSrc && (
                <Image
                  alt={imageFourthAlt || ''}
                  className={classNameFourthImage}
                  height={imageFourthHeight}
                  src={imageFourthSrc}
                  width={imageFourthWidth}
                />
              )}
              {imageFifthSrc && (
                <Image
                  alt={imageFifthAlt || ''}
                  className={classNameFifthImage}
                  height={imageFifthHeight}
                  src={imageFifthSrc}
                  width={imageFifthWidth}
                />
              )}
              {imageSixthSrc && (
                <Image
                  alt={imageSixthAlt || ''}
                  className={classNameSixthImage}
                  height={imageSixthHeight}
                  src={imageSixthSrc}
                  width={imageSixthWidth}
                />
              )}
            </div>
          )}

          {heroSubComponent}

          {feedbackOptions ? (
            <FeedbackMessage className="-mt-8 text-xs" {...feedbackOptions} />
          ) : null}

          <AssessmentActions actions={actions} isCTALoading={isCTALoading} />
        </div>
      </div>

      {overlayComponent}
    </PageLayout>
  );
}
